import {
  ModelContextSettings,
  type ModelContextPolicy,
} from "./model-context-settings";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiGet, apiPut } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function ProjectModelSharingDialog({
  open,
  onClose,
  projectId,
  embedded = false,
}: {
  embedded?: boolean;
  open: boolean;
  onClose(): void;
  projectId: string;
}) {
  const fr = useI18n().language === "fr";
  const client = useQueryClient();
  const models = useQuery({
    queryKey: ["ai-project-models", projectId],
    enabled: open,
    queryFn: () =>
      apiGet<{
        connections: {
          id: string;
          label: string;
          enabled: boolean;
          modelConfigs: {
            id: string;
            modelId: string;
            enabled: boolean;
            capabilities?: { contextPolicy?: ModelContextPolicy };
          }[];
        }[];
        ownSharedModelIds: string[];
      }>(`/ai/projects/${projectId}/models`),
  });
  const share = useMutation({
    mutationFn: ({ id, shared }: { id: string; shared: boolean }) =>
      apiPut(`/ai/projects/${projectId}/models/${id}/share`, { shared }),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: ["ai-project-models", projectId],
      });
    },
  });
  const contents = (
    <div className="space-y-4">
      {" "}
      <p className="text-sm text-foreground/70">
        {fr
          ? "Partagez un de vos modèles avec les membres de ce projet. Les requêtes utiliseront votre clé et seront facturées sur votre compte fournisseur. Vous pouvez retirer cet accès à tout moment."
          : "Share one of your models with this project. Requests use your key and are billed to your provider account. You can revoke access at any time."}
      </p>
      <Link
        to="/settings#ai"
        className="text-primary text-sm"
        onClick={onClose}
      >
        {fr
          ? "Configurer mes fournisseurs dans les paramètres personnels"
          : "Configure my providers in personal settings"}
      </Link>
      {models.data?.connections.flatMap((connection) =>
        connection.modelConfigs
          .filter(
            (model) =>
              model.enabled ||
              models.data?.ownSharedModelIds.includes(model.id),
          )
          .map((model) => (
            <div
              key={model.id}
              className="flex items-center gap-3 rounded-lg border border-white/10 p-3 text-sm"
            >
              <Switch
                checked={
                  models.data?.ownSharedModelIds.includes(model.id) ?? false
                }
                disabled={
                  share.isPending ||
                  (!connection.enabled &&
                    !models.data?.ownSharedModelIds.includes(model.id))
                }
                onCheckedChange={(checked) =>
                  share.mutate({ id: model.id, shared: checked })
                }
              />
              <span className="flex-1 min-w-0 break-words">
                {connection.label} · {model.modelId}
              </span>
              <ModelContextSettings
                modelId={model.id}
                name={model.modelId}
                policy={model.capabilities?.contextPolicy}
              />
            </div>
          )),
      )}
      {!models.data?.connections.length && (
        <p className="text-sm text-foreground/70">
          {fr
            ? "Ajoutez d’abord un fournisseur personnel."
            : "Add a personal provider first."}
        </p>
      )}
      {(models.error || share.error) && (
        <p role="alert" className="text-destructive">
          {(models.error || share.error)?.message}
        </p>
      )}
    </div>
  );
  if (embedded) return contents;
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {fr ? "Modèles du projet" : "Project models"}
          </DialogTitle>
        </DialogHeader>
        {contents}
      </DialogContent>
    </Dialog>
  );
}
